import type { SectionReport } from '../../core/types';
import { cn } from '../../lib/cn';
import { scoreTone } from '../../lib/tone';

export interface SectionBarProps {
  section: SectionReport;
  /** Jumps to the matching form section. */
  onSelect?: (section: SectionReport['section']) => void;
}

/**
 * One row of the section breakdown.
 *
 * A `null` score is rendered as "not scored" rather than as a zero-length bar.
 * That distinction is the visible half of the engine's skipped-rule rule: a
 * section nobody could judge — because the profile has no experience entries at
 * all, say — must not look like a section that scored badly.
 */
export function SectionBar({ section, onSelect }: SectionBarProps) {
  const scored = section.score !== null;
  const score = section.score ?? 0;
  const tone = scoreTone(score);
  const findingCount = section.findings.length;

  const content = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-strong">{section.label}</span>
        <span className="flex items-baseline gap-2">
          {findingCount > 0 ? (
            <span className="text-xs text-subtle">
              {findingCount} {findingCount === 1 ? 'fix' : 'fixes'}
            </span>
          ) : null}
          <span
            className={cn('text-sm font-semibold tabular-nums', scored ? tone.text : 'text-subtle')}
          >
            {scored ? score : '—'}
          </span>
        </span>
      </div>

      <div className="mt-1.5 h-2.5 overflow-hidden rounded-pill bg-sunken">
        <div
          className="h-full rounded-pill transition-[width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{
            width: scored ? `${score}%` : '0%',
            backgroundColor: tone.stroke,
            // A colourless shine over the band colour, so the fill has some
            // depth without losing the hue that says which band it is in.
            backgroundImage: 'var(--gradient-sheen)',
          }}
        />
      </div>

      <p className="mt-1 text-xs text-subtle">
        {scored
          ? `${Math.round(section.weight * 100)}% of your overall score`
          : 'Not scored — nothing here to judge yet'}
      </p>
    </>
  );

  if (!onSelect) return <div>{content}</div>;

  return (
    <button
      type="button"
      onClick={() => onSelect(section.section)}
      className="hover-lift w-full rounded-field border border-transparent p-2.5 text-left hover:border-line hover:bg-sunken"
    >
      {content}
      <span className="sr-only-live">Jump to the {section.label} section of the form</span>
    </button>
  );
}
